/**
 * Login form with username/password authentication and master key option (dev mode)
 * Enhanced with dynamic branding from config
 */

import React, { useState, useMemo, useEffect } from 'react';
import { Button } from '../UI/Button';
import { Input } from '../UI/Input';
import { useAuthContext } from './AuthProvider';
import { RegisterForm } from './RegisterForm';
import { ForgotPassword } from './ForgotPassword';
import { useAuthStore } from '../../stores/auth';
import { getAPIBaseUrl } from '../../api/client';
import { useToastStore } from '../../stores/toast';
import { Key, Shield, Database, Mail, Lock, Eye, EyeOff, Code } from 'lucide-react';
import { TraceKitConfig } from '../../types/config';

const isDev = import.meta.env.DEV || import.meta.env.MODE === 'development';

// Helper function to convert hex color to RGB for opacity
function hexToRgb(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return 'rgb(59, 130, 246)'; // Default blue
  return `rgb(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)})`;
}

// Helper function to generate gradient colors from primary and secondary
function generateGradientColors(primary: string, secondary: string): {
  from: string;
  via: string;
  to: string;
  fromRgb: string;
  viaRgb: string;
  toRgb: string;
} {
  const primaryRgb = hexToRgb(primary);
  const secondaryRgb = hexToRgb(secondary);
  
  // Create intermediate color by mixing
  const primaryMatch = primaryRgb.match(/\d+/g);
  const secondaryMatch = secondaryRgb.match(/\d+/g);
  
  if (primaryMatch && secondaryMatch) {
    const viaR = Math.round((parseInt(primaryMatch[0]) + parseInt(secondaryMatch[0])) / 2);
    const viaG = Math.round((parseInt(primaryMatch[1]) + parseInt(secondaryMatch[1])) / 2);
    const viaB = Math.round((parseInt(primaryMatch[2]) + parseInt(secondaryMatch[2])) / 2);
    const viaRgb = `rgb(${viaR}, ${viaG}, ${viaB})`;
    
    return {
      from: primary,
      via: `rgb(${viaR}, ${viaG}, ${viaB})`,
      to: secondary,
      fromRgb: primaryRgb,
      viaRgb,
      toRgb: secondaryRgb,
    };
  }
  
  return {
    from: primary,
    via: primary,
    to: secondary,
    fromRgb: primaryRgb,
    viaRgb: primaryRgb,
    toRgb: secondaryRgb,
  };
}

interface LoginFormProps {
  config?: TraceKitConfig;
}

export function LoginForm({ config }: LoginFormProps = {}) {
  const [showRegister, setShowRegister] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showMasterKey, setShowMasterKey] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [masterKey, setMasterKey] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const { login, isValidating } = useAuthContext();
  const { addToast } = useToastStore();

  // Check for error messages in URL (from OAuth redirects)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const error = urlParams.get('error');
    if (error) {
      addToast({
        type: 'error',
        message: decodeURIComponent(error),
      });
      // Clear error from URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [addToast]);

  // Extract branding values with fallbacks
  const branding = useMemo(() => {
    const brandingConfig = config?.branding || {};
    const primaryColor = brandingConfig.primaryColor || '#3B82F6'; // Default blue
    const secondaryColor = brandingConfig.secondaryColor || '#8B5CF6'; // Default purple
    
    return {
      appName: brandingConfig.appName || 'TraceKit',
      logoUrl: brandingConfig.logoUrl || brandingConfig.logo || null,
      logoAlt: brandingConfig.logoAlt || brandingConfig.appName || 'Logo',
      platformTitle: brandingConfig.platformTitle || brandingConfig.appName || 'TraceKit',
      platformSubtitle: brandingConfig.platformSubtitle || brandingConfig.companyName || 'Evidence-Driven Intelligence Platform',
      poweredBy: brandingConfig.platformTitle || brandingConfig.appName || 'TraceOS AI',
      primaryColor,
      secondaryColor,
      gradient: generateGradientColors(primaryColor, secondaryColor),
    };
  }, [config]);

  // Determine if we should show logo image or icon
  const hasLogoImage = branding.logoUrl && (branding.logoUrl.startsWith('http') || branding.logoUrl.startsWith('/'));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isValidating) return;
    
    // Master key login (dev mode only)
    if (showMasterKey && masterKey.trim()) {
      try {
        // Test master key by making a simple authenticated API call
        const testResponse = await fetch('http://localhost:8000/users', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${masterKey.trim()}`,
            'Content-Type': 'application/json',
          },
        });
        
        // Master key is valid if we get 200 (users found) or 404 (no users but endpoint exists)
        // 401 means invalid key
        if (testResponse.status === 200 || testResponse.status === 404) {
          // Store master key as access token
          const { setAccessToken } = useAuthStore.getState();
          setAccessToken(masterKey.trim());
          
          // Also set in API client
          const { getAPIClient } = await import('../../api/client');
          const client = getAPIClient();
          client.setAccessToken(masterKey.trim());
          
          // Try to fetch the real user by email
          try {
            const usersResponse = await client.get('/users', { 
              params: { active_only: true } 
            });
            const users = usersResponse.data?.users || [];
            const parsaUser = users.find((u: any) => u.email === 'parsa@alpyne.ai');
            
            if (parsaUser) {
              // Use the real user ID
              useAuthStore.getState().setUser({
                id: parsaUser.id,
                email: parsaUser.email,
                username: parsaUser.username,
                full_name: parsaUser.full_name,
                is_email_verified: parsaUser.is_email_verified,
              });
            } else {
              // Fallback to placeholder if user not found
              useAuthStore.getState().setUser({
                id: 'master-key-user',
                email: 'parsa@alpyne.ai',
                username: 'parsa',
                full_name: 'Parsa Riahi',
                is_email_verified: true,
              });
            }
          } catch (error) {
            // If fetching users fails, use placeholder
            console.warn('Could not fetch user by email, using placeholder:', error);
            useAuthStore.getState().setUser({
              id: 'master-key-user',
              email: 'parsa@alpyne.ai',
              username: 'parsa',
              full_name: 'Parsa Riahi',
              is_email_verified: true,
            });
          }
          
          // Reload to trigger auth check
          window.location.reload();
        } else if (testResponse.status === 401) {
          throw new Error('Invalid master key. Please check your key.');
        } else {
          throw new Error(`Authentication failed: ${testResponse.status}`);
        }
      } catch (error: any) {
        alert(error.message || 'Failed to login with master key. Please check your key.');
      }
      return;
    }
    
    // Username/password login
    if (!username.trim() || !password.trim()) return;
    
    try {
      await login({
        username: username.trim(),
        password,
        remember_me: rememberMe,
      });
      // Navigate to home page after successful login
      window.location.href = '/';
    } catch (error) {
      // Error handling is done in useAuth hook
    }
  };

  // Show forgot password form if toggled
  if (showForgotPassword) {
    // Check for token in URL
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get('token');
    return (
      <ForgotPassword 
        config={config} 
        onBack={() => {
          setShowForgotPassword(false);
          // Clear token from URL
          const newUrl = window.location.pathname;
          window.history.replaceState({}, '', newUrl);
        }}
        initialToken={tokenParam || undefined}
      />
    );
  }

  // Show register form if toggled
  if (showRegister) {
    return <RegisterForm onSuccess={() => setShowRegister(false)} config={config} />;
  }

  // Generate dynamic background gradient style
  const backgroundGradientStyle = {
    background: `linear-gradient(to bottom right, ${branding.gradient.fromRgb}15, ${branding.gradient.viaRgb}15, ${branding.gradient.toRgb}15)`,
  };

  // Generate dynamic logo container gradient style
  const logoGradientStyle = {
    background: `linear-gradient(to bottom right, ${branding.primaryColor}, ${branding.secondaryColor})`,
    boxShadow: `0 20px 25px -5px ${branding.primaryColor}50, 0 10px 10px -5px ${branding.primaryColor}30`,
  };

  // Generate dynamic title gradient style
  const titleGradientStyle = {
    background: `linear-gradient(to right, #0f172a, ${branding.primaryColor}, ${branding.secondaryColor})`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={backgroundGradientStyle}
    >
      <div className="absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] -z-10" />
      
      <div className="w-full max-w-md space-y-8 animate-slide-in">
        {/* Logo and header */}
        <div className="text-center">
          <div 
            className="inline-flex p-4 rounded-2xl mb-6"
            style={logoGradientStyle}
          >
            {hasLogoImage ? (
              <img 
                src={branding.logoUrl!} 
                alt={branding.logoAlt}
                className="h-12 w-12 object-contain"
              />
            ) : (
              <Database className="h-12 w-12 text-white" />
            )}
          </div>
          <h1 
            className="text-4xl font-bold bg-clip-text text-transparent"
            style={titleGradientStyle}
          >
            {branding.appName}
          </h1>
          <p className="mt-3 text-lg text-slate-600 font-medium">
            {branding.platformSubtitle}
          </p>
          <p className="mt-2 text-sm text-slate-500">
            Powered by {branding.poweredBy}
          </p>
        </div>

        {/* Login card */}
        <div className="bg-white/80 backdrop-blur-xl shadow-2xl rounded-2xl p-8 border border-slate-200/50">
          {/* Toggle between login methods */}
          {isDev && (
            <div className="mb-6 flex gap-2 p-1 bg-slate-100 rounded-lg">
              <button
                type="button"
                onClick={() => setShowMasterKey(false)}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  !showMasterKey
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Key className="h-4 w-4 inline mr-2" />
                Username/Password
              </button>
              <button
                type="button"
                onClick={() => setShowMasterKey(true)}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  showMasterKey
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Code className="h-4 w-4 inline mr-2" />
                Master Key
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {showMasterKey ? (
              /* Master Key Login (Dev Mode) */
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Master API Key
                </label>
                <Input
                  type="password"
                  value={masterKey}
                  onChange={(e) => setMasterKey(e.target.value)}
                  placeholder="Enter master API key"
                  leftIcon={<Code className="h-5 w-5 text-slate-400" />}
                  className="h-12 text-base"
                  autoComplete="off"
                  required
                />
                <p className="mt-2 text-xs text-slate-500">
                  Development mode only. Use your master API key for quick access.
                </p>
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Username or Email
                  </label>
                  <Input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Enter your username or email"
                    leftIcon={<Mail className="h-5 w-5 text-slate-400" />}
                    className="h-12 text-base"
                    autoComplete="username"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      leftIcon={<Lock className="h-5 w-5 text-slate-400" />}
                      rightIcon={
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="p-1 hover:bg-slate-100 rounded"
                        >
                          {showPassword ? (
                            <EyeOff className="h-5 w-5 text-slate-400" />
                          ) : (
                            <Eye className="h-5 w-5 text-slate-400" />
                          )}
                        </button>
                      }
                      className="h-12 text-base pr-12"
                      autoComplete="current-password"
                      required
                    />
                  </div>
                </div>
              </>
            )}

            {!showMasterKey && (
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="w-4 h-4 border-slate-300 rounded"
                    style={{
                      accentColor: branding.primaryColor,
                    }}
                  />
                  <span className="text-sm text-slate-600">Remember me</span>
                </label>
                <button
                  type="button"
                  className="text-sm font-medium transition-colors"
                  style={{ color: branding.primaryColor }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = branding.secondaryColor;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = branding.primaryColor;
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    setShowForgotPassword(true);
                  }}
                >
                  Forgot password?
                </button>
              </div>
            )}

            <Button
              type="submit"
              disabled={
                showMasterKey 
                  ? !masterKey.trim() || isValidating
                  : !username.trim() || !password.trim() || isValidating
              }
              className="w-full h-12 text-base font-semibold transition-all transform hover:scale-[1.02] text-white"
              style={{
                background: `linear-gradient(to right, ${branding.primaryColor}, ${branding.secondaryColor})`,
                boxShadow: `0 10px 15px -3px ${branding.primaryColor}30, 0 4px 6px -2px ${branding.primaryColor}20`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = `linear-gradient(to right, ${branding.primaryColor}dd, ${branding.secondaryColor}dd)`;
                e.currentTarget.style.boxShadow = `0 20px 25px -5px ${branding.primaryColor}40, 0 10px 10px -5px ${branding.primaryColor}30`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = `linear-gradient(to right, ${branding.primaryColor}, ${branding.secondaryColor})`;
                e.currentTarget.style.boxShadow = `0 10px 15px -3px ${branding.primaryColor}30, 0 4px 6px -2px ${branding.primaryColor}20`;
              }}
            >
              {isValidating ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Signing in...
                </>
              ) : (
                <>
                  {showMasterKey ? <Code className="h-5 w-5" /> : <Key className="h-5 w-5" />}
                  Sign In
                </>
              )}
            </Button>
          </form>

          {/* SSO Login Options */}
          {!showMasterKey && (
            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white/80 text-slate-500">Or continue with</span>
                </div>
              </div>
              
              <div className="mt-4 grid grid-cols-2 gap-3">
                {/* Azure AD SSO */}
                <Button
                  type="button"
                  onClick={() => {
                    const apiBaseUrl = getAPIBaseUrl();
                    window.location.href = `${apiBaseUrl}/auth/azure/login`;
                  }}
                  className="w-full h-11 text-sm font-medium bg-white border border-slate-300 text-black hover:bg-slate-50 hover:border-slate-400 shadow-sm transition-all duration-200 flex items-center justify-center gap-2 group"
                >
                  {/* Official Microsoft Logo */}
                  <svg 
                    className="h-5 w-5 transition-transform group-hover:scale-110" 
                    viewBox="0 0 23 23" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M0 0H10.5V10.5H0V0Z" fill="#F25022"/>
                    <path d="M12.5 0H23V10.5H12.5V0Z" fill="#7FBA00"/>
                    <path d="M0 12.5H10.5V23H0V12.5Z" fill="#00A4EF"/>
                    <path d="M12.5 12.5H23V23H12.5V12.5Z" fill="#FFB900"/>
                  </svg>
                  <span className="font-semibold text-black">Microsoft</span>
                </Button>
                
                {/* Google SSO */}
                <Button
                  type="button"
                  onClick={() => {
                    const apiBaseUrl = getAPIBaseUrl();
                    window.location.href = `${apiBaseUrl}/auth/google/login`;
                  }}
                  className="w-full h-11 text-sm font-medium bg-white border border-slate-300 text-black hover:bg-slate-50 hover:border-slate-400 shadow-sm transition-all duration-200 flex items-center justify-center gap-2 group"
                >
                  <svg 
                    className="h-5 w-5 transition-transform group-hover:scale-110" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  <span className="font-semibold text-black">Google</span>
                </Button>
              </div>
            </div>
          )}

          <div className="mt-6 pt-6 border-t border-slate-200">
            <p className="text-xs text-center text-slate-500">
              Don't have an account?{' '}
              <button
                type="button"
                className="font-medium transition-colors"
                style={{ color: branding.primaryColor }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = branding.secondaryColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = branding.primaryColor;
                }}
                onClick={() => setShowRegister(true)}
              >
                Sign up
              </button>
            </p>
            <p className="text-xs text-center text-slate-500 mt-2">
              Need help? Contact your administrator
            </p>
          </div>
        </div>

        {/* Features list */}
        <div className="grid grid-cols-3 gap-4 text-center">
          <div className="p-4 bg-white/60 backdrop-blur-sm rounded-xl border border-slate-200/50">
            <div 
              className="text-2xl font-bold bg-clip-text text-transparent"
              style={{
                background: `linear-gradient(to right, ${branding.primaryColor}, ${branding.secondaryColor})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              100%
            </div>
            <div className="text-xs text-slate-600 font-medium mt-1">Evidence-Backed</div>
          </div>
          <div className="p-4 bg-white/60 backdrop-blur-sm rounded-xl border border-slate-200/50">
            <div 
              className="text-2xl font-bold bg-clip-text text-transparent"
              style={{
                background: `linear-gradient(to right, ${branding.primaryColor}, ${branding.secondaryColor})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              AI
            </div>
            <div className="text-xs text-slate-600 font-medium mt-1">Powered Insights</div>
          </div>
          <div className="p-4 bg-white/60 backdrop-blur-sm rounded-xl border border-slate-200/50">
            <div 
              className="text-2xl font-bold bg-clip-text text-transparent"
              style={{
                background: `linear-gradient(to right, ${branding.primaryColor}, ${branding.secondaryColor})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Fast
            </div>
            <div className="text-xs text-slate-600 font-medium mt-1">Real-Time Query</div>
          </div>
        </div>
      </div>
    </div>
  );
}
